import React, { useState, useEffect } from 'react';
import { Snackbar, Button, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { withLang } from '_core/hocs/withLang';
import * as api from '_core/utils/api';

const RawUpdateNotification = (props) => {
  const { getLangString: l } = props;
  const theme = useTheme();
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const hadController = !!navigator.serviceWorker.controller;
      
      // Обработка смены контроллера Service Worker
      // Перезагружаем страницу только если это обновление, а не первая установка
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing && hadController) {
          refreshing = true;
          window.location.reload();
        }
      });

      // Проверяем наличие обновлений при загрузке
      navigator.serviceWorker.ready.then((reg) => {
        // Проверяем наличие обновления
        reg.update();

        // Слушаем события обновления
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                // Проверяем, есть ли уже активный Service Worker
                if (navigator.serviceWorker.controller) {
                  // Новый Service Worker установлен, но еще не активирован
                  setRegistration(reg);
                  setShowUpdate(true);
                } else {
                  // Service Worker установлен впервые
                  console.log('[UpdateNotification] Service Worker installed for the first time');
                }
              }
            });
          }
        });
      });
    }
  }, []);

  const handleUpdate = () => {
    if (registration && registration.waiting) {
      // Очищаем кеш языковых данных и HTML перед обновлением
      api.clearLangCache();
      api.clearHtmlCache();
      
      // Отправляем сообщение Service Worker для пропуска ожидания
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setShowUpdate(false);
    // Страница перезагрузится автоматически при смене контроллера
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <Snackbar
      open={showUpdate}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={null}
      onClose={handleDismiss}
    >
            <Alert
        severity="info"
        action={
          <>
            <Button 
              size="small" 
              onClick={handleUpdate}
              sx={{
                backgroundColor: theme.variables.colors.primary,
                color: theme.variables.colors.primaryContrastText,
                marginRight: '10px',
                border: `1px solid ${theme.variables.colors.primaryContrastText}`,
                '&:hover': {
                  backgroundColor: theme.variables.colors.primary,
                  opacity: 0.9,
                },
              }}
            >
              {l('r._.update.notification.action.update')}
            </Button>
            <Button 
              size="small" 
              onClick={handleDismiss}
              sx={{
                backgroundColor: theme.variables.colors.secondary,
                color: theme.variables.colors.secondaryContrastText,
                border: `1px solid ${theme.variables.colors.secondaryContrastText}`,
                '&:hover': {
                  backgroundColor: theme.variables.colors.secondary,
                  opacity: 0.9,
                },
              }}
            >
              {l('r._.update.notification.action.later')}
            </Button>
          </>
        }
        sx={{ 
          minWidth: '300px',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        {l('r._.update.notification.message')}
      </Alert>
    </Snackbar>
  );
};

RawUpdateNotification.propTypes = {
  getLangString: PropTypes.func.isRequired,
};

export const UpdateNotification = withLang(RawUpdateNotification);
